import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RiCheckLine, RiDeleteBinLine, RiMicLine, RiStopFill, RiUploadCloud2Line } from 'react-icons/ri';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../services/apiConfig';
import styles from './StoryTimeWizard.module.css';

/* Star in your own series (user-voice branch, backend/app/story_time_voice_routes.py): record or upload a reading of a fixed
   passage, hear it back, and opt the series lead into that voice. Self-contained: fetches its own /api/story-time/voice state
   (existing saved voice, if any) and reports up only the one thing the wizard needs -- whether "useUserVoice" should ride on
   the /jobs payload -- via onUseChange. Nothing here runs unless config.userVoice.enabled (checked by the caller). */

const MIN_LEVEL_WARN = 0.03;   // too-quiet warning threshold (peak amplitude, 0..1)
const MAX_LEVEL_WARN = 0.97;   // too-loud warning threshold

const GENDER_LABEL = { female: 'she', male: 'he', neutral: 'they' };

export default function StoryTimeVoiceStep({ passage, minSeconds, maxSeconds, onUseChange, lang }) {
  const auth = useAuth();
  const voiceLang = lang || 'en';
  const [state, setState] = useState(null);      // GET /voice response, or null while loading
  const [phase, setPhase] = useState('idle');     // idle | recording | recorded | uploading | saved
  const [use, setUse] = useState(false);          // the "cast me as the lead" checkbox
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [levelWarning, setLevelWarning] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [peakLevel, setPeakLevel] = useState(0);  // live meter, 0..1
  const [genderBusy, setGenderBusy] = useState(false);

  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const audioCtx = useRef(null);
  const analyser = useRef(null);
  const rafId = useRef(null);
  const timerId = useRef(null);
  const startedAt = useRef(0);
  const streamRef = useRef(null);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState('');
  const fileInputRef = useRef(null);

  const token = useCallback(async () => auth.getToken(), [auth]);

  const loadState = useCallback(async () => {
    try {
      const t = await token();
      const res = await fetch(getApiUrl(`/api/story-time/voice?lang=${voiceLang}`), { headers: { Authorization: `Bearer ${t}` } });
      const data = await res.json();
      setState(data);
      if (data.hasVoice) setUse(true);
    } catch (e) { setState({ hasVoice: false }); }
  }, [token, voiceLang]);

  useEffect(() => { loadState(); setPhase('idle'); setBlob(null); setUse(false); }, [loadState]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onUseChange(use && !!((state && state.hasVoice) || phase === 'saved')); }, [use, state, phase, onUseChange]);
  useEffect(() => () => {
    clearInterval(timerId.current); cancelAnimationFrame(rafId.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (audioCtx.current) audioCtx.current.close().catch(() => {});
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  }, [blobUrl]);

  const meterTick = () => {
    if (!analyser.current) return;
    const data = new Uint8Array(analyser.current.fftSize);
    analyser.current.getByteTimeDomainData(data);
    let peak = 0;
    for (let i = 0; i < data.length; i++) { const v = Math.abs(data[i] - 128) / 128; if (v > peak) peak = v; }
    setPeakLevel((prev) => Math.max(peak, prev * 0.7)); // a little decay so the bar reads as a level, not a flicker
    rafId.current = requestAnimationFrame(meterTick);
  };

  const startRecording = async () => {
    setError(''); setLevelWarning('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtx.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const an = ctx.createAnalyser(); an.fftSize = 512; src.connect(an); analyser.current = an;
      const rec = new MediaRecorder(stream);
      chunks.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.current.push(e.data); };
      rec.onstop = () => {
        const b = new Blob(chunks.current, { type: rec.mimeType || 'audio/webm' });
        setBlob(b); setBlobUrl(URL.createObjectURL(b)); setPhase('recorded');
        stream.getTracks().forEach((t) => t.stop());
        cancelAnimationFrame(rafId.current);
        if (peakLevel < MIN_LEVEL_WARN) setLevelWarning('That sounded very quiet. You can still upload it, or record again a little closer to the mic.');
        else if (peakLevel > MAX_LEVEL_WARN) setLevelWarning('That sounded very loud (it may be clipped). You can still upload it, or record again a little further from the mic.');
      };
      mediaRecorder.current = rec;
      rec.start();
      setPhase('recording'); setElapsed(0); startedAt.current = Date.now();
      timerId.current = setInterval(() => {
        const s = (Date.now() - startedAt.current) / 1000;
        setElapsed(s);
        if (s >= maxSeconds + 3) stopRecording(); // a hard stop well past the passage's length -- never leave the mic open
      }, 100);
      meterTick();
    } catch (e) {
      setError('We could not reach your microphone. Please allow microphone access, or upload a file instead.');
    }
  };

  const stopRecording = () => {
    clearInterval(timerId.current);
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') mediaRecorder.current.stop();
  };

  const reset = () => {
    setPhase('idle'); setBlob(null); if (blobUrl) URL.revokeObjectURL(blobUrl); setBlobUrl(''); setError(''); setLevelWarning(''); setPeakLevel(0);
  };

  const onFilePicked = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setError(''); setLevelWarning(''); setBlob(f); setBlobUrl(URL.createObjectURL(f)); setPhase('recorded');
  };

  const upload = async () => {
    if (!blob) return;
    if (!consent) { setError('Please confirm this is your voice, or that you have permission to use it.'); return; }
    setPhase('uploading'); setError('');
    try {
      const t = await token();
      const form = new FormData();
      const ext = (blob.type || '').includes('wav') ? 'wav' : (blob.type || '').includes('mp3') ? 'mp3' : 'webm';
      form.append('file', blob, `voice.${ext}`);
      form.append('consent', 'true');
      form.append('lang', voiceLang);
      const res = await fetch(getApiUrl('/api/story-time/voice'), { method: 'POST', headers: { Authorization: `Bearer ${t}` }, body: form });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'We could not save that recording.');
      setPhase('saved'); setUse(true); await loadState();
    } catch (e) {
      setError(e.message || 'We could not save that recording. Please try again.'); setPhase('recorded');
    }
  };

  const remove = async () => {
    setError('');
    try {
      const t = await token();
      await fetch(getApiUrl(`/api/story-time/voice?lang=${voiceLang}`), { method: 'DELETE', headers: { Authorization: `Bearer ${t}` } });
      setUse(false); reset(); await loadState();
    } catch (e) { setError('We could not delete your saved voice. Please try again.'); }
  };

  const pickGender = async (gender) => {
    if (genderBusy || (state && state.leadGender === gender)) return;
    setGenderBusy(true); setError('');
    try {
      const t = await token();
      const res = await fetch(getApiUrl('/api/story-time/voice/gender'), {
        method: 'PATCH', headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ lang: voiceLang, gender }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'We could not save that.');
      setState((s) => (s ? { ...s, leadGender: gender } : s));
    } catch (e) {
      setError(e.message || 'We could not save that. Please try again.');
    } finally { setGenderBusy(false); }
  };

  const hasSaved = !!(state && state.hasVoice) && phase !== 'recorded' && phase !== 'recording';
  const canRecord = typeof window !== 'undefined' && !!(navigator.mediaDevices && window.MediaRecorder);

  return (
    <div className={styles.summary} style={{ padding: '16px 18px' }}>
      <div className={styles.summaryRow} style={{ gridTemplateColumns: '1fr', gap: 10 }}>
        <span>Star in it: your voice as the lead <em style={{ fontWeight: 400 }}>Optional</em></span>

        {hasSaved ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label className={styles.requestsLabel} style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
              <input type="checkbox" checked={use} onChange={(e) => setUse(e.target.checked)} />
              Cast my saved voice as the series lead{state.durationS ? ` (${state.durationS.toFixed(1)}s saved)` : ''}
            </label>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
              You'll be written as:{' '}
              {['female', 'male', 'neutral'].map((g) => (
                <button key={g} type="button" onClick={() => pickGender(g)} disabled={genderBusy}
                  style={{ font: 'inherit', border: 'none', background: 'none', padding: '0 3px', cursor: genderBusy ? 'default' : 'pointer',
                    fontWeight: (state.leadGender || 'neutral') === g ? 700 : 400,
                    textDecoration: (state.leadGender || 'neutral') === g ? 'underline' : 'none',
                    color: (state.leadGender || 'neutral') === g ? 'var(--primary-color)' : 'var(--text-secondary)' }}>
                  {GENDER_LABEL[g]}
                </button>
              )).reduce((acc, el, i) => (i === 0 ? [el] : [...acc, ' / ', el]), [])}
            </p>
            <button type="button" className={styles.link} onClick={remove}><RiDeleteBinLine size={14} style={{ verticalAlign: -2 }} /> Delete my saved voice</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p className={styles.lede} style={{ margin: 0 }}>Read this out loud (about {minSeconds}-{maxSeconds} seconds), and we cast your own voice as the lead character.</p>
            <blockquote style={{ margin: 0, padding: '10px 14px', borderLeft: '3px solid var(--primary-color)', fontStyle: 'italic', color: 'var(--text-primary)' }}>{passage}</blockquote>

            {phase === 'idle' && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {canRecord && <button type="button" className={styles.primary} onClick={startRecording}><RiMicLine size={16} /> Record</button>}
                <button type="button" className={styles.ghost} onClick={() => fileInputRef.current && fileInputRef.current.click()}><RiUploadCloud2Line size={16} /> Upload a file</button>
                <input ref={fileInputRef} type="file" accept="audio/*" style={{ display: 'none' }} onChange={onFilePicked} />
              </div>
            )}

            {phase === 'recording' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ height: 8, borderRadius: 4, background: 'var(--border-color)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(100, peakLevel * 130)}%`, background: peakLevel > MAX_LEVEL_WARN ? '#b3261e' : 'var(--primary-color)', transition: 'width .08s linear' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button type="button" className={styles.primary} onClick={stopRecording}><RiStopFill size={16} /> Stop ({elapsed.toFixed(0)}s)</button>
                  <span className={styles.waitNote}>Read the passage above, then stop.</span>
                </div>
              </div>
            )}

            {(phase === 'recorded' || phase === 'uploading') && blobUrl && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <audio controls src={blobUrl} style={{ width: '100%' }} />
                {levelWarning && <p className={styles.error} role="alert">{levelWarning}</p>}
                <label className={styles.requestsLabel} style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} disabled={phase === 'uploading'} />
                  This is my voice, or I have permission to use it.
                </label>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button type="button" className={styles.primary} disabled={phase === 'uploading' || !consent} onClick={upload}>
                    {phase === 'uploading' ? 'Saving…' : <><RiCheckLine size={16} /> Use this recording</>}
                  </button>
                  <button type="button" className={styles.ghost} disabled={phase === 'uploading'} onClick={reset}>Re-record</button>
                </div>
              </div>
            )}
          </div>
        )}
        {error && <p className={styles.error} role="alert">{error}</p>}
      </div>
    </div>
  );
}
